import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import SuperAdminLayout from '../components/SuperAdminLayout';
import axios from 'axios';
import { Download, FileText, FileSpreadsheet } from 'lucide-react';
import toast from '../utils/toast';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminExport = () => {
  const { t } = useTranslation();
  const [exportData, setExportData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchExportData();
  }, []);

  const fetchExportData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/export/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExportData(response.data);
    } catch (error) {
      console.error('Failed to fetch export data:', error);
      toast.error('Kunne ikke hente eksportdata');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (exportData.length === 0) {
      toast.error('Ingen data å eksportere');
      return;
    }

    // Create CSV header
    const headers = [
      'Organisasjonsnavn',
      'Kontaktperson e-post',
      'Kontaktperson navn',
      'Kontaktperson telefon',
      'Besøksadresse + Poststed'
    ];

    // Create CSV rows
    const csvRows = [
      headers.join(','),
      ...exportData.map(org => [
        `"${org.organization_name}"`,
        `"${org.contact_email}"`,
        `"${org.contact_person}"`,
        `"${org.contact_phone}"`,
        `"${org.address_and_city}"`
      ].join(','))
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `organisasjoner_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success('CSV-fil lastet ned!');
  };

  const downloadPDF = () => {
    if (exportData.length === 0) {
      toast.error('Ingen data å eksportere');
      return;
    }

    const doc = new jsPDF();
    
    // Infovegg colors
    const infoveggTeal = [80, 227, 194]; // #50E3C2
    const darkGray = [42, 42, 42]; // #2A2A2A
    const lightGray = [243, 243, 243]; // #F3F3F3

    // Add title
    doc.setFontSize(20);
    doc.setTextColor(...darkGray);
    doc.text('Organisasjoner - Kontaktinformasjon', 14, 22);

    // Add export date
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text(`Eksportert: ${new Date().toLocaleDateString('no-NO')}`, 14, 30);

    // Prepare table data
    const tableData = exportData.map(org => [
      org.organization_name,
      org.contact_email,
      org.contact_person,
      org.contact_phone,
      org.address_and_city
    ]);

    // Add table with Infovegg styling
    autoTable(doc, {
      head: [[
        'Organisasjonsnavn',
        'Kontakt e-post',
        'Kontaktperson',
        'Telefon',
        'Adresse + Poststed'
      ]],
      body: tableData,
      startY: 35,
      styles: {
        fontSize: 9,
        cellPadding: 3,
        lineColor: [200, 200, 200],
        lineWidth: 0.1
      },
      headStyles: {
        fillColor: infoveggTeal,
        textColor: darkGray,
        fontStyle: 'bold',
        halign: 'left'
      },
      alternateRowStyles: {
        fillColor: lightGray
      },
      margin: { top: 35, left: 14, right: 14 }
    });

    // Add footer with page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Side ${i} av ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    doc.save(`organisasjoner_export_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF-fil lastet ned!');
  };

  return (
    <SuperAdminLayout>
      <div className="admin-header">
        <h1>{t('superAdmin.export.title')}</h1>
        <button 
          className="btn btn-secondary" 
          onClick={fetchExportData}
          disabled={loading}
        >
          {loading ? t('superAdmin.export.updating') : t('superAdmin.export.updateData')}
        </button>
      </div>

      <div style={{ 
        background: '#1a1f2e', 
        padding: '2rem', 
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{ color: 'white', marginBottom: '1rem' }}>{t('superAdmin.export.aboutExport')}</h2>
        <p style={{ color: '#a0a0a0', marginBottom: '1rem', lineHeight: '1.6' }}>
          {t('superAdmin.export.aboutText')}
        </p>
        <ul style={{ color: '#e0e0e0', marginLeft: '1.5rem', lineHeight: '1.8' }}>
          <li>{t('superAdmin.export.fields.organizationName')}</li>
          <li>{t('superAdmin.export.fields.contactEmail')}</li>
          <li>{t('superAdmin.export.fields.contactPerson')}</li>
          <li>{t('superAdmin.export.fields.contactPhone')}</li>
          <li>{t('superAdmin.export.fields.address')}</li>
        </ul>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: '#1a1f2e',
          padding: '2rem',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center'
        }}>
          <FileSpreadsheet size={48} color="#50E3C2" style={{ marginBottom: '1rem' }} />
          <h3 style={{ color: 'white', marginBottom: '1rem' }}>{t('superAdmin.export.csvTitle')}</h3>
          <p style={{ color: '#a0a0a0', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            {t('superAdmin.export.csvDescription')}
          </p>
          <button 
            className="btn btn-primary" 
            onClick={downloadCSV}
            disabled={loading || exportData.length === 0}
            style={{ width: '100%' }}
          >
            <Download size={20} style={{ marginRight: '0.5rem' }} />
            {t('superAdmin.export.downloadCsv')}
          </button>
        </div>

        <div style={{
          background: '#1a1f2e',
          padding: '2rem',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center'
        }}>
          <FileText size={48} color="#50E3C2" style={{ marginBottom: '1rem' }} />
          <h3 style={{ color: 'white', marginBottom: '1rem' }}>{t('superAdmin.export.pdfTitle')}</h3>
          <p style={{ color: '#a0a0a0', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            {t('superAdmin.export.pdfDescription')}
          </p>
          <button 
            className="btn btn-primary" 
            onClick={downloadPDF}
            disabled={loading || exportData.length === 0}
            style={{ width: '100%' }}
          >
            <Download size={20} style={{ marginRight: '0.5rem' }} />
            {t('superAdmin.export.downloadPdf')}
          </button>
        </div>
      </div>

      <div style={{
        background: '#1a1f2e',
        padding: '1.5rem',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <h3 style={{ color: 'white', marginBottom: '1rem' }}>{t('superAdmin.export.preview')}</h3>
        
        {loading ? (
          <p style={{ color: '#a0a0a0', textAlign: 'center', padding: '2rem' }}>
            {t('superAdmin.export.loading')}
          </p>
        ) : exportData.length === 0 ? (
          <p style={{ color: '#a0a0a0', textAlign: 'center', padding: '2rem' }}>
            {t('superAdmin.export.noOrganizations')}
          </p>
        ) : (
          <>
            <p style={{ color: '#a0a0a0', marginBottom: '1rem' }}>
              {t('superAdmin.export.totalReady', { count: exportData.length })}
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: '800px' }}>
                <thead>
                  <tr>
                    <th style={{ 
                      padding: '0.75rem',
                      textAlign: 'left',
                      color: '#50E3C2',
                      borderBottom: '2px solid rgba(80, 227, 194, 0.3)',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      {t('superAdmin.export.tableHeaders.organization')}
                    </th>
                    <th style={{ 
                      padding: '0.75rem',
                      textAlign: 'left',
                      color: '#50E3C2',
                      borderBottom: '2px solid rgba(80, 227, 194, 0.3)',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      {t('superAdmin.export.tableHeaders.email')}
                    </th>
                    <th style={{ 
                      padding: '0.75rem',
                      textAlign: 'left',
                      color: '#50E3C2',
                      borderBottom: '2px solid rgba(80, 227, 194, 0.3)',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      {t('superAdmin.export.tableHeaders.contactPerson')}
                    </th>
                    <th style={{ 
                      padding: '0.75rem',
                      textAlign: 'left',
                      color: '#50E3C2',
                      borderBottom: '2px solid rgba(80, 227, 194, 0.3)',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      {t('superAdmin.export.tableHeaders.phone')}
                    </th>
                    <th style={{ 
                      padding: '0.75rem',
                      textAlign: 'left',
                      color: '#50E3C2',
                      borderBottom: '2px solid rgba(80, 227, 194, 0.3)',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      {t('superAdmin.export.tableHeaders.address')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {exportData.map((org, index) => (
                    <tr 
                      key={index}
                      style={{
                        borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                      }}
                    >
                      <td style={{ 
                        padding: '0.75rem',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}>
                        {org.organization_name}
                      </td>
                      <td style={{ 
                        padding: '0.75rem',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}>
                        {org.contact_email}
                      </td>
                      <td style={{ 
                        padding: '0.75rem',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}>
                        {org.contact_person}
                      </td>
                      <td style={{ 
                        padding: '0.75rem',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}>
                        {org.contact_phone}
                      </td>
                      <td style={{ 
                        padding: '0.75rem',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}>
                        {org.address_and_city}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminExport;
